"use client";

import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import type { OEmbedProvider } from "@/lib/utils/oembed";

export type GlobalEmbedProvider = Exclude<OEmbedProvider, "instagram" | "x" | null>;

export interface GlobalEmbedPayload {
	url: string;
	provider: GlobalEmbedProvider;
	title?: string;
	thumbnailUrl?: string;
}

interface GlobalEmbedPlayerState {
	payload: GlobalEmbedPayload | null;
	isOpen: boolean;
}

interface GlobalEmbedPlayerContextValue extends GlobalEmbedPlayerState {
	openEmbed: (payload: GlobalEmbedPayload) => void;
	closeEmbed: () => void;
}

const GlobalEmbedPlayerContext =
	createContext<GlobalEmbedPlayerContextValue | null>(null);

export function GlobalEmbedPlayerProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<GlobalEmbedPlayerState>({
		payload: null,
		isOpen: false,
	});

	const openEmbed = useCallback((payload: GlobalEmbedPayload) => {
		setState({ payload, isOpen: true });
	}, []);

	const closeEmbed = useCallback(() => {
		setState((prev) => ({ ...prev, isOpen: false }));
	}, []);

	const value = useMemo<GlobalEmbedPlayerContextValue>(
		() => ({
			...state,
			openEmbed,
			closeEmbed,
		}),
		[state, openEmbed, closeEmbed],
	);

	return (
		<GlobalEmbedPlayerContext.Provider value={value}>
			{children}
		</GlobalEmbedPlayerContext.Provider>
	);
}

export function useGlobalEmbedPlayer(): GlobalEmbedPlayerContextValue {
	const ctx = useContext(GlobalEmbedPlayerContext);
	if (!ctx) {
		throw new Error(
			"useGlobalEmbedPlayer must be used within GlobalEmbedPlayerProvider",
		);
	}
	return ctx;
}
